import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Gem, Play, Square, RefreshCw, TrendingUp, TrendingDown,
  Activity, DollarSign, Target, Shield, Loader2, Clock,
  ArrowUpRight, ArrowDownRight, Zap, Trophy, BarChart3,
  HeartPulse, AlertTriangle, RotateCcw, Rocket, CheckCircle,
  Bot, Power, Globe, RotateCw, Radio,
  Crown, Swords,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { HowToGuide } from "../components/HowToGuide";
import { RiskEnginePanel, PromotionLadderPanel, RegimeFirewallPanel, DecayDetectorPanel } from "../components/SafetyDashboard";

import { API } from '../config';

const REGIME_LABELS = {
  TREND_UP: { text: "text-emerald-400", label: "Trend Up" },
  TREND_DOWN: { text: "text-red-400", label: "Trend Down" },
  RANGE_LOW_VOL: { text: "text-blue-400", label: "Range LV" },
  RANGE_HIGH_VOL: { text: "text-orange-400", label: "Range HV" },
  BREAKOUT_EXPANSION: { text: "text-yellow-400", label: "Breakout" },
  MEAN_REVERTIVE_CHOP: { text: "text-violet-400", label: "Mean Rev" },
};

const PnlText = ({ value, className = "" }) => {
  if (value === null || value === undefined) return <span className="text-zinc-500">---</span>;
  const isPositive = value >= 0;
  return (
    <span className={`font-mono ${isPositive ? "text-emerald-400" : "text-red-400"} ${className}`}>
      {isPositive ? "+" : ""}${Number(value).toFixed(2)}
    </span>
  );
};

/* ── Survivor Selection Panel ── */
const SurvivorPanel = ({ survivors, currentRegime, onDeploy, deploying }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [balance, setBalance] = useState(100000);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === survivors.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(survivors.map(s => s.strategy_name)));
    }
  };

  const handleDeploy = () => {
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : [];
    onDeploy(ids, balance);
  };

  const regimeMatched = survivors.filter(s => s.regime_match);

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="survivor-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-bold text-zinc-200 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" /> Validated Survivors
              <Badge className="text-[8px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                {survivors.length} available
              </Badge>
            </h2>
            <p className="text-[10px] text-zinc-500 mt-1">
              Current regime: <span className={`font-bold ${REGIME_LABELS[currentRegime]?.text || "text-zinc-400"}`}>
                {REGIME_LABELS[currentRegime]?.label || currentRegime}
              </span>
              {regimeMatched.length > 0 && (
                <span className="text-emerald-400 ml-2">({regimeMatched.length} regime-matched)</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={balance}
              onChange={(e) => setBalance(Number(e.target.value))}
              className="bg-zinc-900 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm h-9 w-28 font-mono"
              data-testid="deploy-balance-input"
            />
            <Button
              size="sm"
              onClick={selectAll}
              variant="outline"
              className="border-zinc-700 text-zinc-400 h-9"
              data-testid="select-all-btn"
            >
              {selectedIds.size === survivors.length ? "Deselect" : "Select All"}
            </Button>
            <Button
              size="sm"
              onClick={handleDeploy}
              disabled={deploying}
              className="bg-emerald-700 hover:bg-emerald-600 text-white h-9"
              data-testid="deploy-btn"
            >
              {deploying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Rocket className="w-3 h-3 mr-1" />}
              Deploy {selectedIds.size > 0 ? `(${selectedIds.size})` : "Auto"}
            </Button>
          </div>
        </div>

        {survivors.length === 0 && (
          <div className="text-center py-8">
            <Shield className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm mb-1">No validated survivors available</p>
            <p className="text-zinc-600 text-xs">
              Go to Strategy Registry and run the 5-gate validation pipeline to discover survivors.
            </p>
          </div>
        )}

        {survivors.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {survivors.map(s => {
              const rl = REGIME_LABELS[s.regime] || { text: "text-zinc-400", label: s.regime };
              const selected = selectedIds.has(s.strategy_name);
              return (
                <div
                  key={s.strategy_id}
                  onClick={() => toggleSelect(s.strategy_name)}
                  className={`cursor-pointer border p-3 transition-all ${
                    selected
                      ? "border-emerald-500/60 bg-emerald-950/20"
                      : s.regime_match
                        ? "border-emerald-700/20 bg-zinc-800/40 hover:border-emerald-600/40"
                        : "border-zinc-700/30 bg-zinc-800/40 hover:border-zinc-600"
                  }`}
                  data-testid={`survivor-card-${s.strategy_name}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-zinc-200 font-bold truncate">{s.strategy_name}</span>
                    <div className="flex items-center gap-1.5">
                      {s.regime_match && <Badge className="text-[7px] bg-emerald-500/20 text-emerald-300">MATCH</Badge>}
                      {selected && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Sharpe</span>
                      <span className={`font-mono ${s.sharpe >= 2.5 ? "text-violet-400" : "text-amber-400"}`}>{s.sharpe?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Regime</span>
                      <span className={`font-mono ${rl.text}`}>{rl.label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Asset</span>
                      <span className="font-mono text-zinc-300">{s.asset?.replace("USDT", "")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Family</span>
                      <span className="font-mono text-zinc-300">{s.family}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Agent Card ── */
const AgentCard = ({ agent }) => {
  if (!agent) return null;
  const pos = agent.open_position;
  const health = agent.health || {};
  const healthColor = {
    HEALTHY: "bg-emerald-500/20 text-emerald-300",
    WARNING: "bg-yellow-500/20 text-yellow-300",
    CRITICAL: "bg-red-500/20 text-red-300",
    PAUSED: "bg-orange-500/20 text-orange-300",
  }[health.status] || "bg-zinc-700/40 text-zinc-400";

  return (
    <Card className={`border ${agent.is_running ? "bg-gradient-to-br from-cyan-950/20 to-zinc-900/50 border-cyan-700/30" : agent.health?.paused_by_health ? "bg-gradient-to-br from-orange-950/20 to-zinc-900/50 border-orange-700/30" : "bg-zinc-900/50 border-zinc-800"}`} data-testid={`agent-card-${agent.name}`}>
      <CardHeader className="py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-zinc-100 flex items-center gap-2">
            <Gem className="w-4 h-4 text-cyan-400" />
            {agent.name}
            <Badge className={`text-[10px] ${agent.is_running ? "bg-emerald-500/20 text-emerald-300" : agent.health?.paused_by_health ? "bg-orange-500/20 text-orange-300" : "bg-zinc-700/40 text-zinc-400"}`}>
              {agent.health?.paused_by_health ? "PAUSED" : agent.is_running ? "LIVE" : "STOPPED"}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {health.status && (
              <Badge className={`text-[10px] ${healthColor}`} data-testid={`health-badge-${agent.name}`}>
                <HeartPulse className="w-3 h-3 mr-1 inline" />{health.status}
              </Badge>
            )}
            <span className="text-xs text-zinc-500">{agent.symbol}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4 space-y-3">
        <div className="grid grid-cols-5 gap-3 text-xs">
          <div>
            <p className="text-zinc-500 text-[10px]">Balance</p>
            <p className="font-mono text-lg font-bold text-zinc-100">${agent.balance?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-[10px]">Total P&L</p>
            <PnlText value={agent.total_pnl} className="text-lg font-bold" />
          </div>
          <div>
            <p className="text-zinc-500 text-[10px]">P&L %</p>
            <PnlText value={agent.total_pnl_pct} className="text-sm" />
          </div>
          <div>
            <p className="text-zinc-500 text-[10px]">Win Rate</p>
            <p className="font-mono text-sm text-zinc-300">{agent.win_rate?.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-zinc-500 text-[10px]">Trades</p>
            <p className="font-mono text-sm text-zinc-300">{agent.total_trades} <span className="text-emerald-400">({agent.wins}W</span>/<span className="text-red-400">{agent.losses}L)</span></p>
          </div>
        </div>

        {pos && (
          <div className={`p-3 border ${pos.side === "LONG" ? "bg-emerald-950/20 border-emerald-800/30" : "bg-red-950/20 border-red-800/30"}`} data-testid={`open-pos-${agent.name}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {pos.side === "LONG" ? <ArrowUpRight className="w-4 h-4 text-emerald-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                <span className={`text-sm font-bold ${pos.side === "LONG" ? "text-emerald-400" : "text-red-400"}`}>{pos.side}</span>
                <span className="text-xs text-zinc-400">{pos.symbol}</span>
              </div>
              <PnlText value={pos.unrealized_pnl} className="text-sm font-bold" />
            </div>
            <div className="grid grid-cols-4 gap-2 text-[10px]">
              <div><span className="text-zinc-500">Entry:</span> <span className="font-mono text-zinc-300">${pos.entry_price?.toFixed(2)}</span></div>
              <div><span className="text-zinc-500">Current:</span> <span className="font-mono text-zinc-300">${pos.current_price?.toFixed(2)}</span></div>
              <div><span className="text-zinc-500">SL:</span> <span className="font-mono text-red-400">${pos.sl_price?.toFixed(2)}</span></div>
              <div><span className="text-zinc-500">TP:</span> <span className="font-mono text-emerald-400">${pos.tp_price?.toFixed(2)}</span></div>
            </div>
          </div>
        )}

        {!pos && agent.is_running && (
          <div className="text-center py-2 text-xs text-zinc-500">
            <Activity className="w-4 h-4 inline mr-1 animate-pulse" /> Scanning for signals...
          </div>
        )}

        {health.rolling_trades > 0 && (
          <div className="grid grid-cols-4 gap-3 text-xs border-t border-zinc-800 pt-2">
            <div>
              <p className="text-zinc-500 text-[10px] flex items-center gap-1"><HeartPulse className="w-2.5 h-2.5" /> Sharpe (R30)</p>
              <p className={`font-mono ${health.rolling_sharpe >= 1.5 ? "text-emerald-400" : health.rolling_sharpe >= 1.0 ? "text-yellow-400" : "text-red-400"}`}>{health.rolling_sharpe?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px]">Win% (R30)</p>
              <p className={`font-mono ${health.rolling_win_rate >= 40 ? "text-emerald-400" : "text-red-400"}`}>{health.rolling_win_rate?.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px]">Drawdown</p>
              <p className={`font-mono ${health.drawdown_pct <= 10 ? "text-zinc-300" : "text-red-400"}`}>{health.drawdown_pct?.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px]">Consec. Loss</p>
              <p className={`font-mono ${health.consecutive_losses < 3 ? "text-zinc-300" : "text-red-400"}`}>{health.consecutive_losses}</p>
            </div>
          </div>
        )}

        {(health.critical_reasons?.length > 0 || health.warning_reasons?.length > 0) && (
          <div className={`text-[10px] px-2 py-1.5 ${health.critical_reasons?.length ? "bg-red-950/30 border border-red-800/30" : "bg-yellow-950/30 border border-yellow-800/30"}`}>
            {health.critical_reasons?.map((r, i) => (
              <div key={i} className="flex items-center gap-1 text-red-400"><AlertTriangle className="w-3 h-3 shrink-0" /> {r}</div>
            ))}
            {health.warning_reasons?.map((r, i) => (
              <div key={i} className="flex items-center gap-1 text-yellow-400"><AlertTriangle className="w-3 h-3 shrink-0" /> {r}</div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] text-zinc-600">
          <span>Ticks: {agent.tick_count}</span>
          <span>Signals: {agent.signals_generated}</span>
          {agent.last_check && <span>Last: {new Date(agent.last_check).toLocaleTimeString()}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

/* ── Degradation Tracking Panel ── */
const DegradationPanel = ({ agents, token }) => {
  const [cards, setCards] = useState({});
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchCards = async () => {
      setLoading(true);
      const agentNames = Object.keys(agents);
      const results = {};
      for (const name of agentNames) {
        const a = agents[name];
        const sid = `${name}_${a.symbol || "ETHUSDT"}_4h_gen0`;
        try {
          const r = await axios.get(`${API}/regime/survivor-card/${sid}`, { headers });
          results[name] = r.data;
        } catch {
          // Try with just the name
          try {
            const r2 = await axios.get(`${API}/regime/survivor-card/${name}`, { headers });
            results[name] = r2.data;
          } catch {}
        }
      }
      setCards(results);
      setLoading(false);
    };
    if (Object.keys(agents).length > 0) fetchCards();
    else setLoading(false);
  }, [Object.keys(agents).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex items-center gap-2 text-zinc-500 text-xs py-4"><Loader2 className="w-3 h-3 animate-spin" /> Loading degradation data...</div>;
  if (Object.keys(cards).length === 0) return null;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="degradation-panel">
      <CardHeader className="py-3 px-5">
        <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-amber-400" /> Live Degradation Tracking
          <span className="text-[10px] text-zinc-500 font-normal ml-1">Backtest vs Real-World Performance</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4 space-y-4">
        {Object.entries(cards).map(([name, card]) => {
          const agent = agents[name] || {};
          const le = card.live_estimates || {};
          const pt = card.paper_tracking || {};
          const p2 = card.performance?.["2yr"] || {};
          const fees = card.fee_assumptions || {};

          const backtestAPY = le.backtest_apy || p2.apy || 0;
          const conservativeAPY = le.conservative_apy || 0;
          const worstCaseAPY = le.worst_case_apy || 0;

          // Compute live APY from agent data if available
          const agentPnlPct = agent.total_pnl_pct || 0;
          const tickHours = (agent.tick_count || 0) * 0.5; // ~30s ticks, rough estimate
          const liveAPY = tickHours > 0 ? (agentPnlPct / Math.max(tickHours / 8760, 0.001)) : 0;

          // Use paper tracking APY if available
          const paperAPY = pt.paper_apy || 0;
          const displayAPY = paperAPY || liveAPY;

          const degradationPct = backtestAPY > 0 ? Math.round((1 - displayAPY / backtestAPY) * 100) : 0;
          const maxBar = Math.max(Math.abs(backtestAPY), Math.abs(conservativeAPY), Math.abs(displayAPY), 1);

          return (
            <div key={name} className="border border-zinc-800/50 p-4" data-testid={`degradation-${name}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-xs text-zinc-200 font-bold">{name}</span>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[8px] ${card.regime_match ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                    {card.target_regime} {card.regime_match ? "ACTIVE" : `(live: ${card.current_regime})`}
                  </Badge>
                </div>
              </div>

              {/* Visual bar comparison */}
              <div className="space-y-2.5 mb-4">
                <BarRow label="Backtest APY" value={backtestAPY} maxBar={maxBar} color="bg-cyan-500" />
                <BarRow label="Conservative (60%)" value={conservativeAPY} maxBar={maxBar} color="bg-amber-500" />
                <BarRow label="Worst Case (40%)" value={worstCaseAPY} maxBar={maxBar} color="bg-red-500" />
                <BarRow label={paperAPY ? "Paper Trading APY" : "Live APY (est.)"} value={displayAPY} maxBar={maxBar} color="bg-emerald-500" highlight />
              </div>

              {/* Degradation metric */}
              <div className="grid grid-cols-4 gap-3 text-center mb-3">
                <div className="bg-cyan-950/20 border border-cyan-800/20 p-2">
                  <p className="text-[9px] text-cyan-400 uppercase">Backtest</p>
                  <p className="text-base font-bold font-mono text-cyan-400">{backtestAPY.toFixed(1)}%</p>
                </div>
                <div className="bg-amber-950/20 border border-amber-800/20 p-2">
                  <p className="text-[9px] text-amber-400 uppercase">Expected</p>
                  <p className="text-base font-bold font-mono text-amber-400">{conservativeAPY.toFixed(1)}%</p>
                </div>
                <div className={`border p-2 ${displayAPY >= worstCaseAPY ? "bg-emerald-950/20 border-emerald-800/20" : "bg-red-950/20 border-red-800/20"}`}>
                  <p className={`text-[9px] uppercase ${displayAPY >= worstCaseAPY ? "text-emerald-400" : "text-red-400"}`}>{paperAPY ? "Paper" : "Live"}</p>
                  <p className={`text-base font-bold font-mono ${displayAPY >= worstCaseAPY ? "text-emerald-400" : "text-red-400"}`}>{displayAPY.toFixed(1)}%</p>
                </div>
                <div className={`border p-2 ${Math.abs(degradationPct) < 50 ? "bg-emerald-950/20 border-emerald-800/20" : "bg-red-950/20 border-red-800/20"}`}>
                  <p className="text-[9px] text-zinc-400 uppercase">Degradation</p>
                  <p className={`text-base font-bold font-mono ${Math.abs(degradationPct) < 50 ? "text-amber-400" : "text-red-400"}`}>{degradationPct}%</p>
                </div>
              </div>

              {/* Fee breakdown */}
              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <div className="text-zinc-500">Fees/side: <span className="text-zinc-300 font-mono">{fees.paper_fee_per_side || "0.04%"}</span></div>
                <div className="text-zinc-500">Slippage: <span className="text-zinc-300 font-mono">{fees.paper_slippage || "0.03%"}</span></div>
                <div className="text-zinc-500">Funding: <span className="text-zinc-300 font-mono">{fees.paper_funding_rate || "0.01%/8h"}</span></div>
              </div>

              {/* Paper tracking status */}
              {pt.active && (
                <div className="mt-2 pt-2 border-t border-zinc-800/40 flex items-center gap-4 text-[10px]">
                  <span className="flex items-center gap-1"><span className="flex h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" /> Paper tracking active</span>
                  <span className="text-zinc-500">Days: <span className="text-zinc-200 font-bold">{pt.days_tracked}</span></span>
                  <span className="text-zinc-500">Trades: <span className="text-zinc-200 font-bold">{pt.paper_trades}</span></span>
                  <span className="text-zinc-500">P&L: <span className={`font-bold ${pt.paper_pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>${pt.paper_pnl?.toFixed(2)}</span></span>
                  <span className="text-zinc-500">Fees paid: <span className="text-red-400 font-bold">${pt.total_fees_paid?.toFixed(2)}</span></span>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const BarRow = ({ label, value, maxBar, color, highlight = false }) => {
  const pct = maxBar > 0 ? Math.max(Math.abs(value) / maxBar * 100, 2) : 2;
  return (
    <div className={`flex items-center gap-3 ${highlight ? "py-1" : ""}`}>
      <span className={`text-[10px] w-32 shrink-0 text-right ${highlight ? "text-zinc-200 font-bold" : "text-zinc-500"}`}>{label}</span>
      <div className="flex-1 h-4 bg-zinc-800/60 overflow-hidden relative">
        <div className={`h-full ${color} ${highlight ? "opacity-90" : "opacity-60"} transition-all`} style={{ width: `${pct}%` }} />
        {highlight && <div className="absolute inset-0 border border-white/10" />}
      </div>
      <span className={`text-xs font-mono w-16 text-right ${highlight ? "text-zinc-100 font-bold" : "text-zinc-400"}`}>
        {value >= 0 ? "+" : ""}{value.toFixed(1)}%
      </span>
    </div>
  );
};

/* ── Regime Coverage Panel ── */
const RegimeCoveragePanel = ({ token }) => {
  const [coverage, setCoverage] = useState(null);
  const [mineStatus, setMineStatus] = useState(null);
  const [launching, setLaunching] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCoverage = async () => {
    try {
      const [cov, ms] = await Promise.all([
        axios.get(`${API}/diamond-agents/regime-coverage`, { headers }),
        axios.get(`${API}/diamond-agents/diversity-mine/status`, { headers }),
      ]);
      setCoverage(cov.data);
      setMineStatus(ms.data);
    } catch {}
  };

  useEffect(() => { fetchCoverage(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mineStatus?.running) return;
    const iv = setInterval(fetchCoverage, 15000);
    return () => clearInterval(iv);
  }, [mineStatus?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const launchDiversityMine = async () => {
    setLaunching(true);
    try {
      const res = await axios.post(`${API}/diamond-agents/diversity-mine?include_candidates=true`, {}, { headers });
      toast.success(`Diversity mining launched for ${res.data.total_gaps} regime gaps!`);
      fetchCoverage();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to launch"); }
    finally { setLaunching(false); }
  };

  const stopMining = async () => {
    try {
      await axios.post(`${API}/diamond-agents/diversity-mine/stop`, {}, { headers });
      toast.success("Diversity mining stopping...");
      fetchCoverage();
    } catch {}
  };

  if (!coverage) return null;

  const summary = coverage.summary || {};
  const items = coverage.coverage || [];
  const mining = mineStatus?.running;

  const STATUS_STYLE = {
    COVERED: { bg: "bg-emerald-500/10 border-emerald-700/30", icon: CheckCircle, color: "text-emerald-400" },
    CANDIDATES: { bg: "bg-amber-500/10 border-amber-700/30", icon: Target, color: "text-amber-400" },
    GAP: { bg: "bg-red-500/10 border-red-700/30", icon: AlertTriangle, color: "text-red-400" },
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="regime-coverage-panel">
      <CardHeader className="py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
            <Shield className="w-4 h-4 text-violet-400" /> Regime Coverage
            <Badge className={`text-[8px] ml-1 ${summary.coverage_pct >= 80 ? "bg-emerald-500/20 text-emerald-300" : summary.coverage_pct >= 50 ? "bg-amber-500/20 text-amber-300" : "bg-red-500/20 text-red-300"}`}>
              {summary.coverage_pct}% covered
            </Badge>
            <span className="text-[10px] text-zinc-500 font-normal ml-1">
              {summary.covered}/{summary.total_regimes} regimes with survivors
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {mining ? (
              <Button size="sm" onClick={stopMining} className="bg-red-700 hover:bg-red-600 text-white h-8 text-xs" data-testid="stop-diversity-mine-btn">
                <Square className="w-3 h-3 mr-1" /> Stop Mining
              </Button>
            ) : summary.gaps > 0 || summary.has_candidates > 0 ? (
              <Button size="sm" onClick={launchDiversityMine} disabled={launching} className="bg-violet-700 hover:bg-violet-600 text-white h-8 text-xs" data-testid="launch-diversity-mine-btn">
                {launching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                Mine {summary.gaps + summary.has_candidates} Gaps
              </Button>
            ) : (
              <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                <CheckCircle className="w-3 h-3 mr-1 inline" /> Full Coverage
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {items.map(item => {
            const style = STATUS_STYLE[item.status] || STATUS_STYLE.GAP;
            const rl = REGIME_LABELS[item.regime] || { text: "text-zinc-400", label: item.regime };
            const Icon = style.icon;
            const isMining = mining && mineStatus?.current_regime === item.regime;
            return (
              <div key={item.regime} className={`border p-2.5 ${style.bg} ${isMining ? "ring-1 ring-violet-500/50" : ""}`} data-testid={`coverage-${item.regime}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-bold ${rl.text}`}>{rl.label || item.regime}</span>
                  <Icon className={`w-3.5 h-3.5 ${style.color}`} />
                </div>
                {item.has_survivor ? (
                  <div className="text-[9px] space-y-0.5">
                    <div className="text-emerald-400 font-mono truncate">{item.best_survivor}</div>
                    <div className="text-zinc-500">Sharpe {item.best_sharpe?.toFixed(2)} | {item.survivor_count} survivor{item.survivor_count > 1 ? "s" : ""}</div>
                  </div>
                ) : item.emerald_candidates > 0 ? (
                  <div className="text-[9px] space-y-0.5">
                    <div className="text-amber-400">{item.emerald_candidates} EMERALDs (unvalidated)</div>
                    <div className="text-zinc-500">Avg Sharpe {item.avg_emerald_sharpe?.toFixed(2)}</div>
                  </div>
                ) : (
                  <div className="text-[9px] text-red-400">No strategies found</div>
                )}
                {isMining && (
                  <div className="mt-1 text-[8px] text-violet-400 flex items-center gap-1">
                    <Loader2 className="w-2.5 h-2.5 animate-spin" /> Mining...
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mining Progress */}
        {mining && mineStatus && (
          <div className="mt-3 p-3 bg-violet-950/20 border border-violet-800/30" data-testid="diversity-mine-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-violet-300 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Diversity Mining Active
              </span>
              <span className="text-[10px] text-zinc-400 font-mono">
                {mineStatus.progress_pct}% | {mineStatus.completed_regimes?.length}/{mineStatus.target_regimes?.length} regimes
              </span>
            </div>
            <div className="w-full bg-zinc-800 h-1.5 overflow-hidden mb-2">
              <div className="h-full bg-violet-500 transition-all" style={{ width: `${mineStatus.progress_pct}%` }} />
            </div>
            {/* Discovery summary */}
            {Object.keys(mineStatus.discoveries || {}).length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-[9px]">
                {Object.entries(mineStatus.discoveries).map(([regime, disc]) => (
                  <div key={regime} className="text-center">
                    <span className="text-zinc-500 block">{regime.split("_").slice(0, 2).join(" ")}</span>
                    <span className={`font-mono font-bold ${disc.emeralds > 0 ? "text-emerald-400" : disc.diamonds > 0 ? "text-violet-400" : disc.golds > 0 ? "text-amber-400" : "text-zinc-600"}`}>
                      {disc.emeralds}E {disc.diamonds}D {disc.golds}G
                    </span>
                  </div>
                ))}
              </div>
            )}
            {/* Mining log */}
            {mineStatus.log?.length > 0 && (
              <div className="mt-2 max-h-20 overflow-y-auto space-y-0.5">
                {mineStatus.log.slice(-5).map((entry, i) => (
                  <div key={i} className="text-[9px] text-zinc-500 font-mono">{entry}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Auto-Validator Panel ── */
const AutoValidatorPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [toggling, setToggling] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/diamond-agents/auto-validate/status`, { headers });
      setStatus(res.data);
    } catch {}
  };

  useEffect(() => { fetchStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!status?.enabled) return;
    const iv = setInterval(fetchStatus, 10000);
    return () => clearInterval(iv);
  }, [status?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async () => {
    setToggling(true);
    try {
      if (status?.enabled) {
        await axios.post(`${API}/diamond-agents/auto-validate/stop`, {}, { headers });
        toast.success("Auto-validator stopped");
      } else {
        await axios.post(`${API}/diamond-agents/auto-validate/start?interval_sec=120`, {}, { headers });
        toast.success("Auto-validator engaged! Validating EMERALDs...");
      }
      await fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setToggling(false); }
  };

  if (!status) return null;

  const enabled = status.enabled;
  const passRate = status.pass_rate || 0;
  const newSurvivors = status.new_survivors || [];

  return (
    <Card className={`border ${enabled ? "bg-gradient-to-r from-amber-950/20 to-emerald-950/20 border-amber-700/30" : "bg-zinc-900/50 border-zinc-800"}`} data-testid="auto-validator-panel">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center border ${enabled ? "bg-amber-500/10 border-amber-600/40" : "bg-zinc-800/60 border-zinc-700"}`}>
              <Shield className={`w-5 h-5 ${enabled ? "text-amber-400" : "text-zinc-600"}`} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-zinc-200 flex items-center gap-2">
                AUTO-VALIDATOR
                {enabled && <span className="flex h-2 w-2 rounded-full bg-amber-400 animate-pulse" />}
                {status.current_strategy && (
                  <span className="text-[10px] text-amber-300 font-mono font-normal ml-1 animate-pulse">
                    Validating: {status.current_strategy}
                  </span>
                )}
              </h2>
              <p className="text-[10px] text-zinc-500">
                {enabled
                  ? `${status.validated_count} validated | ${status.passed_count} passed | ${status.unvalidated_remaining} remaining`
                  : `${status.unvalidated_remaining} unvalidated EMERALDs waiting`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {enabled && status.validated_count > 0 && (
              <div className="flex items-center gap-3 text-[10px]">
                <div className="text-center">
                  <div className="text-emerald-400 font-bold">{status.passed_count}</div>
                  <div className="text-zinc-600">passed</div>
                </div>
                <div className="text-center">
                  <div className="text-red-400 font-bold">{status.failed_count}</div>
                  <div className="text-zinc-600">failed</div>
                </div>
                <div className="text-center">
                  <div className={`font-bold ${passRate > 20 ? "text-emerald-400" : "text-amber-400"}`}>{passRate}%</div>
                  <div className="text-zinc-600">rate</div>
                </div>
              </div>
            )}
            <Button
              size="sm"
              onClick={toggle}
              disabled={toggling}
              className={enabled
                ? "bg-red-700 hover:bg-red-600 text-white"
                : "bg-amber-700 hover:bg-amber-600 text-white"
              }
              data-testid="auto-validator-toggle-btn"
            >
              {toggling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Shield className="w-3.5 h-3.5 mr-1" />}
              {enabled ? "Stop" : "Start Auto-Validate"}
            </Button>
          </div>
        </div>

        {/* New survivors discovered */}
        {newSurvivors.length > 0 && (
          <div className="mt-3 border-t border-zinc-800 pt-2">
            <div className="text-[10px] text-emerald-400 font-bold mb-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> New Survivors Discovered
            </div>
            <div className="flex flex-wrap gap-2">
              {newSurvivors.map((s, i) => {
                const rl = REGIME_LABELS[s.regime] || { text: "text-zinc-400", label: s.regime };
                return (
                  <Badge key={i} className="text-[9px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
                    {s.strategy_name} <span className={`ml-1 ${rl.text}`}>{rl.label}</span> Sharpe {s.sharpe?.toFixed(2)}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Validation log */}
        {enabled && status.log?.length > 0 && (
          <div className="mt-2 max-h-24 overflow-y-auto space-y-0.5 border-t border-zinc-800 pt-2">
            {status.log.slice(-5).map((entry, i) => (
              <div key={i} className={`text-[9px] font-mono ${entry.includes("PASS") ? "text-emerald-500" : entry.includes("FAIL") ? "text-red-400" : "text-zinc-500"}`}>
                {entry}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Equity Curve Panel ── */
const EquityCurvePanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCurve = async () => {
    try {
      const res = await axios.get(`${API}/diamond-agents/equity-curve`, { headers });
      setData(res.data);
    } catch {}
  };

  useEffect(() => { fetchCurve(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const agents = data?.agents || {};
    const hasRunning = Object.values(agents).some(a => a.is_running);
    if (!hasRunning) return;
    const iv = setInterval(fetchCurve, 15000);
    return () => clearInterval(iv);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const deployChampion = async () => {
    setDeploying(true);
    try {
      const res = await axios.post(`${API}/diamond-agents/reset-and-deploy-champion?balance=100000`, {}, { headers });
      toast.success(`Champion ${res.data.champion} deployed with $100K!`);
      fetchCurve();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setDeploying(false); }
  };

  const agents = data?.agents || {};
  const combined = data?.combined || {};
  const ethBench = data?.eth_benchmark || {};
  const hasAgents = Object.keys(agents).length > 0;
  const hasRunning = hasAgents && Object.values(agents).some(a => a.is_running);

  return (
    <Card className={`border ${hasRunning ? "bg-gradient-to-r from-emerald-950/20 to-cyan-950/10 border-emerald-700/30" : "bg-zinc-900/50 border-zinc-800"}`} data-testid="equity-curve-panel">
      <CardHeader className="py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
            <BarChart3 className={`w-4 h-4 ${hasRunning ? "text-emerald-400" : "text-zinc-500"}`} />
            Live Portfolio
            {hasRunning && <span className="flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={fetchCurve} variant="ghost" className="text-zinc-500 h-7 w-7 p-0">
              <RefreshCw className="w-3 h-3" />
            </Button>
            <Button size="sm" onClick={deployChampion} disabled={deploying} className="bg-amber-700 hover:bg-amber-600 text-white h-7 text-xs" data-testid="deploy-champion-btn">
              {deploying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Crown className="w-3 h-3 mr-1" />}
              Deploy Champion ($100K)
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {hasAgents ? (
          <div className="space-y-3">
            {/* Combined Portfolio Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="portfolio-summary">
              <div className="text-center p-2 bg-zinc-800/40 border border-zinc-700/30">
                <div className="text-[9px] text-zinc-500 mb-0.5">Starting Balance</div>
                <div className="text-sm font-mono font-bold text-zinc-200">${combined.total_starting?.toLocaleString()}</div>
              </div>
              <div className="text-center p-2 bg-zinc-800/40 border border-zinc-700/30">
                <div className="text-[9px] text-zinc-500 mb-0.5">Current Balance</div>
                <div className={`text-sm font-mono font-bold ${combined.total_balance >= combined.total_starting ? "text-emerald-400" : "text-red-400"}`}>
                  ${combined.total_balance?.toLocaleString()}
                </div>
              </div>
              <div className="text-center p-2 bg-zinc-800/40 border border-zinc-700/30">
                <div className="text-[9px] text-zinc-500 mb-0.5">P&L</div>
                <div className={`text-sm font-mono font-bold ${combined.total_pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {combined.total_pnl >= 0 ? "+" : ""}${combined.total_pnl?.toLocaleString()}
                </div>
              </div>
              <div className="text-center p-2 bg-zinc-800/40 border border-zinc-700/30">
                <div className="text-[9px] text-zinc-500 mb-0.5">Return %</div>
                <div className={`text-sm font-mono font-bold ${combined.total_pnl_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {combined.total_pnl_pct >= 0 ? "+" : ""}{combined.total_pnl_pct?.toFixed(2)}%
                </div>
              </div>
              <div className="text-center p-2 bg-zinc-800/40 border border-zinc-700/30">
                <div className="text-[9px] text-zinc-500 mb-0.5">ETH B&H</div>
                <div className={`text-sm font-mono font-bold ${(ethBench.return_pct || 0) >= 0 ? "text-cyan-400" : "text-red-400"}`}>
                  {(ethBench.return_pct || 0) >= 0 ? "+" : ""}{(ethBench.return_pct || 0).toFixed(2)}%
                </div>
              </div>
            </div>

            {/* Per-agent details */}
            {Object.values(agents).map(agent => (
              <div key={agent.strategy_name} className={`p-3 border ${agent.is_running ? "border-emerald-700/30 bg-emerald-950/10" : "border-zinc-700/30 bg-zinc-800/20"}`} data-testid={`equity-agent-${agent.strategy_name}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${agent.is_running ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`} />
                    <span className="font-mono text-xs font-bold text-zinc-200">{agent.strategy_name}</span>
                    <span className={`text-[9px] ${agent.is_running ? "text-emerald-400" : "text-zinc-600"}`}>
                      {agent.is_running ? "LIVE" : "STOPPED"}
                    </span>
                  </div>
                  <span className={`font-mono text-sm font-bold ${agent.total_pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {agent.total_pnl >= 0 ? "+" : ""}${agent.total_pnl?.toLocaleString()} ({agent.pnl_pct}%)
                  </span>
                </div>
                <div className="grid grid-cols-6 gap-2 text-[10px]">
                  <div><span className="text-zinc-600">Start:</span> <span className="text-zinc-300 font-mono">${agent.starting_balance?.toLocaleString()}</span></div>
                  <div><span className="text-zinc-600">Current:</span> <span className={`font-mono ${agent.current_balance >= agent.starting_balance ? "text-emerald-400" : "text-red-400"}`}>${agent.current_balance?.toLocaleString()}</span></div>
                  <div><span className="text-zinc-600">Trades:</span> <span className="text-zinc-300 font-mono">{agent.total_trades}</span></div>
                  <div><span className="text-zinc-600">Win Rate:</span> <span className="text-zinc-300 font-mono">{agent.win_rate}%</span></div>
                  <div><span className="text-zinc-600">W/L:</span> <span className="text-emerald-400 font-mono">{agent.wins}</span>/<span className="text-red-400 font-mono">{agent.losses}</span></div>
                  <div><span className="text-zinc-600">MaxDD:</span> <span className="text-red-400 font-mono">{agent.max_drawdown_pct}%</span></div>
                </div>
                {/* Equity curve visualization */}
                {agent.equity_points?.length > 1 && (
                  <div className="mt-2 h-12 flex items-end gap-px" data-testid="equity-chart">
                    {(() => {
                      const pts = agent.equity_points;
                      const maxE = Math.max(...pts.map(p => p.equity));
                      const minE = Math.min(...pts.map(p => p.equity));
                      const range = maxE - minE || 1;
                      const step = Math.max(1, Math.floor(pts.length / 60));
                      const sampled = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
                      return sampled.map((pt, i) => {
                        const h = ((pt.equity - minE) / range) * 100;
                        const isUp = pt.equity >= agent.starting_balance;
                        return <div key={i} className={`flex-1 min-w-[2px] ${isUp ? "bg-emerald-500/60" : "bg-red-500/60"}`} style={{ height: `${Math.max(h, 5)}%` }} />;
                      });
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-[10px] text-zinc-600">
            <DollarSign className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
            <div>No agents deployed. Click "Deploy Champion" to start with a clean $100K portfolio.</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Autopilot Control Panel ── */
const AutopilotPanel = ({ token }) => {
  const [autopilotStatus, setAutopilotStatus] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [toggling, setToggling] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAutopilot = async () => {
    try {
      const [ap, pf] = await Promise.all([
        axios.get(`${API}/diamond-agents/autopilot/status`, { headers }),
        axios.get(`${API}/diamond-agents/portfolio`, { headers }),
      ]);
      setAutopilotStatus(ap.data);
      setPortfolio(pf.data);
    } catch {}
  };

  useEffect(() => { fetchAutopilot(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autopilotStatus?.enabled) return;
    const iv = setInterval(fetchAutopilot, 30000);
    return () => clearInterval(iv);
  }, [autopilotStatus?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAutopilot = async () => {
    setToggling(true);
    try {
      if (autopilotStatus?.enabled) {
        await axios.post(`${API}/diamond-agents/autopilot/stop`, {}, { headers });
        toast.success("Autopilot disengaged");
      } else {
        await axios.post(`${API}/diamond-agents/autopilot/start?interval_sec=300`, {}, { headers });
        toast.success("Autopilot engaged! Monitoring regimes every 5 min.");
      }
      await fetchAutopilot();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setToggling(false); }
  };

  const enabled = autopilotStatus?.enabled;
  const regimes = autopilotStatus?.current_regimes || {};
  const prices = autopilotStatus?.regime_prices || {};
  const regimeMap = portfolio?.regime_map || [];
  const rotations = autopilotStatus?.rotations || [];

  return (
    <div className="space-y-3" data-testid="autopilot-section">
      {/* Autopilot Toggle */}
      <Card className={`border ${enabled ? "bg-gradient-to-r from-cyan-950/30 to-emerald-950/20 border-cyan-700/40" : "bg-zinc-900/50 border-zinc-800"}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center border ${enabled ? "bg-cyan-500/10 border-cyan-600/40" : "bg-zinc-800/60 border-zinc-700"}`}>
                <Bot className={`w-5 h-5 ${enabled ? "text-cyan-400" : "text-zinc-600"}`} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-zinc-200 flex items-center gap-2">
                  AUTOPILOT
                  {enabled && <span className="flex h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />}
                </h2>
                <p className="text-[10px] text-zinc-500">
                  {enabled ? "Monitoring regimes & auto-rotating strategies" : "Manual mode — enable to auto-manage deployments"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {enabled && (
                <div className="text-right text-[10px] text-zinc-500">
                  <div>Check interval: {autopilotStatus?.check_interval_sec || 300}s</div>
                  <div>Rotations: {autopilotStatus?.total_rotations || 0}</div>
                </div>
              )}
              <Button
                size="sm"
                onClick={toggleAutopilot}
                disabled={toggling}
                className={enabled
                  ? "bg-red-700 hover:bg-red-600 text-white"
                  : "bg-cyan-700 hover:bg-cyan-600 text-white"
                }
                data-testid="autopilot-toggle-btn"
              >
                {toggling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Power className="w-3.5 h-3.5 mr-1" />}
                {enabled ? "Disengage" : "Engage Autopilot"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regime Map — Multi-Asset Overview */}
      {(regimeMap.length > 0 || Object.keys(regimes).length > 0) && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="regime-map-panel">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-400" /> Live Regime Map
              <span className="text-[10px] text-zinc-500 font-normal ml-1">Multi-asset regime detection</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {regimeMap.map(rm => {
                const rl = REGIME_LABELS[rm.regime] || { text: "text-zinc-400", label: rm.regime };
                return (
                  <div key={rm.asset} className={`border p-3 ${rm.has_survivor ? "border-emerald-700/30 bg-emerald-950/10" : "border-zinc-700/30 bg-zinc-800/30"}`} data-testid={`regime-card-${rm.asset}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm font-bold text-zinc-200">{rm.asset.replace("USDT", "")}</span>
                      <span className="font-mono text-xs text-zinc-400">${rm.price?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <Radio className={`w-3 h-3 ${rl.text}`} />
                      <span className={`text-sm font-bold ${rl.text}`}>{rl.label}</span>
                      {rm.confidence > 0 && <span className="text-[10px] text-zinc-600 font-mono">{(rm.confidence * 100).toFixed(0)}%</span>}
                    </div>
                    {rm.has_survivor ? (
                      <div className="text-[10px] space-y-0.5">
                        <div className="text-emerald-400 font-bold flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Survivor ready
                        </div>
                        <div className="text-zinc-400 font-mono">{rm.best_strategy}</div>
                        <div className="text-zinc-500">
                          Sharpe {rm.best_sharpe?.toFixed(2)} | R-Sharpe {rm.best_regime_sharpe?.toFixed(1)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-[10px] text-zinc-600">
                        <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
                        No survivor for this regime — cash position
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rotation History */}
      {rotations.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="rotation-history">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
              <RotateCw className="w-4 h-4 text-amber-400" /> Rotation History
              <Badge className="text-[8px] bg-amber-500/20 text-amber-300 border-amber-500/40 ml-1">
                {rotations.length} events
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {rotations.slice().reverse().map((r, i) => (
                <div key={i} className="flex items-center gap-3 text-[10px] py-1.5 border-b border-zinc-800/30">
                  <span className="text-zinc-600 w-16 shrink-0">{r.timestamp ? new Date(r.timestamp).toLocaleTimeString() : ""}</span>
                  <Badge className={`text-[9px] w-16 justify-center ${
                    r.action === "DEPLOY" ? "bg-emerald-500/20 text-emerald-300" :
                    r.action === "ROTATE" ? "bg-cyan-500/20 text-cyan-300" :
                    "bg-red-500/20 text-red-300"
                  }`}>{r.action}</Badge>
                  <span className="text-zinc-400 truncate">
                    {r.action === "ROTATE"
                      ? `${r.from_strategy} → ${r.to_strategy} (${r.from_regime} → ${r.to_regime})`
                      : r.action === "DEPLOY"
                        ? `${r.strategy} in ${r.regime} (Sharpe ${r.sharpe?.toFixed(2)})`
                        : `${r.from_strategy} → CASH (${r.reason})`
                    }
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ── Tournament Panel ── */
const TournamentPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [launching, setLaunching] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/diamond-agents/tournament/status`, { headers });
      setStatus(res.data);
    } catch {}
  };

  useEffect(() => { fetchStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!status?.running) return;
    const iv = setInterval(fetchStatus, 8000);
    return () => clearInterval(iv);
  }, [status?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const startTournament = async () => {
    setLaunching(true);
    try {
      const res = await axios.post(`${API}/diamond-agents/tournament/start`, {}, { headers });
      toast.success(`Tournament started with ${res.data.contestants} contestants!`);
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLaunching(false); }
  };

  const stopTournament = async () => {
    try {
      await axios.post(`${API}/diamond-agents/tournament/stop`, {}, { headers });
      toast.success("Tournament stopping...");
      fetchStatus();
    } catch {}
  };

  if (!status) return null;

  const running = status.running;
  const phase = status.phase;
  const champ = status.champion;
  const board = status.leaderboard || [];
  const log = status.log || [];

  const PHASE_LABELS = {
    starting: "Preparing...", round1: "Round 1 — 180 days",
    round2: "Round 2 — 365 days", final: "FINAL — 730 days",
    complete: "Complete",
  };

  return (
    <Card className={`border ${champ ? "bg-gradient-to-r from-amber-950/20 to-yellow-950/20 border-amber-600/40" : running ? "bg-gradient-to-r from-violet-950/20 to-indigo-950/20 border-violet-700/30" : "bg-zinc-900/50 border-zinc-800"}`} data-testid="tournament-panel">
      <CardHeader className="py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
            <Swords className={`w-4 h-4 ${running ? "text-violet-400" : champ ? "text-amber-400" : "text-zinc-500"}`} />
            EVOLUTION TOURNAMENT
            {running && (
              <Badge className="text-[8px] bg-violet-500/20 text-violet-300 border-violet-500/40 ml-1 animate-pulse">
                {PHASE_LABELS[phase] || phase}
              </Badge>
            )}
            {champ && !running && (
              <Badge className="text-[8px] bg-amber-500/20 text-amber-300 border-amber-500/40 ml-1">
                <Crown className="w-2.5 h-2.5 mr-0.5 inline" /> Champion Crowned
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {running ? (
              <Button size="sm" onClick={stopTournament} className="bg-red-700 hover:bg-red-600 text-white h-7 text-xs" data-testid="stop-tournament-btn">
                <Square className="w-3 h-3 mr-1" /> Stop
              </Button>
            ) : (
              <Button size="sm" onClick={startTournament} disabled={launching} className="bg-violet-700 hover:bg-violet-600 text-white h-7 text-xs" data-testid="start-tournament-btn">
                {launching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Swords className="w-3 h-3 mr-1" />}
                Start Tournament
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {/* Champion Card */}
        {champ && (
          <div className="mb-3 p-3 bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border border-amber-600/30" data-testid="champion-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 border border-amber-500/40">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-amber-300 font-bold mb-0.5">CHAMPION</div>
                <div className="text-sm font-mono font-bold text-zinc-100">{champ.strategy_name}</div>
                <div className="flex gap-3 mt-1 text-[10px]">
                  <span className="text-zinc-400">Regime: <span className="text-cyan-400">{champ.regime}</span></span>
                  <span className="text-zinc-400">Fitness: <span className="text-amber-400 font-bold">{champ.fitness?.toFixed(3)}</span></span>
                  <span className="text-zinc-400">Sharpe: <span className="text-emerald-400">{champ.avg_sharpe?.toFixed(2)}</span></span>
                  <span className="text-zinc-400">Return: <span className="text-emerald-400">{champ.avg_return_pct?.toFixed(1)}%</span></span>
                  <span className="text-zinc-400">MaxDD: <span className="text-red-400">{champ.worst_dd_pct?.toFixed(1)}%</span></span>
                  <span className="text-zinc-400">Win Rate: <span className="text-cyan-400">{champ.avg_win_rate?.toFixed(1)}%</span></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {board.length > 0 && (
          <div className="mb-3" data-testid="tournament-leaderboard">
            <div className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Leaderboard
            </div>
            <div className="space-y-1">
              {board.map((entry, i) => {
                const rl = REGIME_LABELS[entry.regime] || { text: "text-zinc-400", label: entry.regime };
                return (
                  <div key={entry.strategy_name} className={`flex items-center gap-2 px-2 py-1.5 text-[10px] border ${entry.is_champion ? "border-amber-600/30 bg-amber-500/5" : "border-zinc-800/50 bg-zinc-900/30"}`} data-testid={`tournament-entry-${i}`}>
                    <span className={`w-5 text-center font-bold ${i === 0 ? "text-amber-400" : i === 1 ? "text-zinc-300" : i === 2 ? "text-orange-400" : "text-zinc-600"}`}>
                      {i === 0 ? <Crown className="w-3 h-3 inline" /> : `#${i + 1}`}
                    </span>
                    <span className="font-mono text-zinc-200 w-36 truncate">{entry.strategy_name}</span>
                    <span className={`w-16 ${rl.text}`}>{rl.label}</span>
                    <span className="text-zinc-500 w-8">R{entry.rounds_played}</span>
                    <div className="flex-1 flex gap-3">
                      {entry.r1_fitness > 0 && <span className="text-zinc-500">R1: <span className="text-violet-400">{entry.r1_fitness.toFixed(2)}</span></span>}
                      {entry.r2_fitness > 0 && <span className="text-zinc-500">R2: <span className="text-indigo-400">{entry.r2_fitness.toFixed(2)}</span></span>}
                      {entry.final_fitness > 0 && <span className="text-zinc-500">F: <span className="text-amber-400 font-bold">{entry.final_fitness.toFixed(2)}</span></span>}
                    </div>
                    <span className={`font-bold ${entry.best_fitness > 3 ? "text-emerald-400" : entry.best_fitness > 2 ? "text-cyan-400" : "text-zinc-400"}`}>
                      {entry.best_fitness.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tournament Log */}
        {log.length > 0 && (
          <div className="max-h-28 overflow-y-auto space-y-0.5 border-t border-zinc-800 pt-2">
            {log.slice(-8).map((entry, i) => (
              <div key={i} className={`text-[9px] font-mono ${entry.includes("CHAMPION") ? "text-amber-400 font-bold" : entry.includes("===") ? "text-violet-400" : "text-zinc-500"}`}>
                {entry}
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!running && !champ && board.length === 0 && (
          <div className="text-center py-4 text-[10px] text-zinc-600">
            <Swords className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
            <div>Launch a tournament to find the absolute best strategy</div>
            <div className="text-zinc-700 mt-1">3 rounds: 180d → 365d → 730d across ETH, BTC, SOL</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   MAIN DIAMOND AGENTS PAGE
   ═══════════════════════════════════════════ */
export default function DiamondAgentsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [agentLog, setAgentLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [survivors, setSurvivors] = useState([]);
  const [currentRegime, setCurrentRegime] = useState("");
  const [leaderboard, setLeaderboard] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [resuming, setResuming] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/diamond-agents/status`, { headers });
      setStatus(res.data);
    } catch {} finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSurvivors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/diamond-agents/survivors`, { headers });
      setSurvivors(res.data?.survivors || []);
      setCurrentRegime(res.data?.current_regime || "");
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTrades = async () => {
    try { const res = await axios.get(`${API}/diamond-agents/trades?limit=30`, { headers }); setTrades(res.data.trades || []); } catch {}
  };

  const fetchLog = async () => {
    try { const res = await axios.get(`${API}/diamond-agents/log?limit=50`, { headers }); setAgentLog(res.data.log || []); } catch {}
  };

  const fetchLeaderboard = async () => {
    try { const res = await axios.get(`${API}/diamond-agents/leaderboard?days=30`, { headers }); setLeaderboard(res.data); } catch {}
  };

  const fetchHealth = async () => {
    try { const res = await axios.get(`${API}/diamond-agents/health`, { headers }); setHealthData(res.data); } catch {}
  };

  const refreshAll = () => { fetchStatus(); fetchSurvivors(); fetchTrades(); fetchLog(); fetchLeaderboard(); fetchHealth(); };

  useEffect(() => { refreshAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status?.status !== "running") return;
    const interval = setInterval(() => { fetchStatus(); fetchTrades(); fetchHealth(); }, 10000);
    return () => clearInterval(interval);
  }, [status?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const deployAgents = async (ids, balance) => {
    try {
      setDeploying(true);
      const idsParam = ids.length > 0 ? ids.join(",") : "";
      await axios.post(`${API}/diamond-agents/deploy?balance=${balance}&strategy_ids=${idsParam}`, {}, { headers });
      toast.success("Agents deployed from registry!");
      refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Deploy failed"); }
    finally { setDeploying(false); }
  };

  const stopAgents = async () => {
    try {
      setStopping(true);
      await axios.post(`${API}/diamond-agents/stop`, {}, { headers });
      toast.success("Agents stopped.");
      refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Stop failed"); }
    finally { setStopping(false); }
  };

  const resumeAgents = async () => {
    try {
      setResuming(true);
      await axios.post(`${API}/diamond-agents/resume`, {}, { headers });
      toast.success("Agents resumed!");
      refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "No paused agents"); }
    finally { setResuming(false); }
  };

  const isRunning = status?.status === "running";
  const agents = status?.agents || {};
  const summary = status?.summary || {};
  const anyPaused = Object.values(agents).some(a => a.health?.paused_by_health);
  const overallHealth = healthData?.overall || "";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="diamond-agents-loading">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
        <span className="ml-3 text-zinc-400 font-mono">Loading agents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="diamond-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Gem className="w-6 h-6 text-cyan-400" />
            DIAMOND Agents
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Deploy validated survivors from Strategy Registry
          </p>
        </div>
        <div className="flex gap-2">
          <HowToGuide />
          <Button variant="outline" size="sm" onClick={refreshAll} className="border-zinc-700 text-zinc-400" data-testid="refresh-agents-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          {anyPaused && (
            <Button size="sm" onClick={resumeAgents} disabled={resuming} className="bg-orange-600 hover:bg-orange-500 text-white" data-testid="resume-agents-btn">
              {resuming ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
              Resume Paused
            </Button>
          )}
          {isRunning && (
            <Button size="sm" onClick={stopAgents} disabled={stopping} className="bg-red-600 hover:bg-red-500 text-white" data-testid="stop-agents-btn">
              {stopping ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Square className="w-3.5 h-3.5 mr-1" />}
              Stop All
            </Button>
          )}
        </div>
      </div>

      {/* Regime Coverage & Diversity Mining */}
      <RegimeCoveragePanel token={token} />

      {/* Auto-Validator */}
      <AutoValidatorPanel token={token} />

      {/* Autopilot & Portfolio */}
      <AutopilotPanel token={token} />

      {/* Live Portfolio & Equity Curve */}
      <EquityCurvePanel token={token} />

      {/* Evolution Tournament */}
      <TournamentPanel token={token} />

      {/* Safety Layer — Risk Engine, Promotion Ladder, Regime Firewall, Decay Detector */}
      <div className="grid grid-cols-2 gap-3">
        <RiskEnginePanel />
        <DecayDetectorPanel />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <PromotionLadderPanel />
        <RegimeFirewallPanel />
      </div>

      {/* Summary Bar (when running) */}
      {(isRunning || summary.total_trades > 0) && (
        <div className="grid grid-cols-5 gap-3">
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-zinc-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Balance</p>
              <p className="font-mono text-lg font-bold text-zinc-100">${summary.total_balance?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-zinc-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> P&L</p>
              <PnlText value={summary.total_pnl} className="text-lg font-bold" />
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Target className="w-3 h-3" /> Win Rate</p>
              <p className="font-mono text-lg font-bold text-zinc-300">{summary.win_rate?.toFixed(1)}%</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Zap className="w-3 h-3" /> Trades</p>
              <p className="font-mono text-lg font-bold text-zinc-300">{summary.total_trades}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-zinc-500 flex items-center gap-1"><Shield className="w-3 h-3" /> Status</p>
              <Badge className={`text-xs mt-1 ${isRunning ? "bg-emerald-500/20 text-emerald-300" : anyPaused ? "bg-orange-500/20 text-orange-300" : "bg-zinc-700/40 text-zinc-400"}`}>
                {anyPaused ? "PAUSED" : isRunning ? "LIVE" : "STOPPED"}
              </Badge>
              {overallHealth && overallHealth !== "INACTIVE" && (
                <Badge className={`text-[9px] mt-1 ml-1 ${overallHealth === "HEALTHY" ? "bg-emerald-500/10 text-emerald-400" : overallHealth === "WARNING" ? "bg-yellow-500/10 text-yellow-400" : overallHealth === "CRITICAL" ? "bg-red-500/10 text-red-400" : "bg-zinc-700/30 text-zinc-400"}`}>
                  <HeartPulse className="w-2.5 h-2.5 mr-0.5 inline" />{overallHealth}
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Survivor Selection (when not running) */}
      {!isRunning && (
        <SurvivorPanel
          survivors={survivors}
          currentRegime={currentRegime}
          onDeploy={deployAgents}
          deploying={deploying}
        />
      )}

      {/* Agent Cards (when running) */}
      {Object.keys(agents).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Object.values(agents).map(agent => (
            <AgentCard key={agent.name} agent={agent} />
          ))}
        </div>
      )}

      {/* Degradation Tracking */}
      {Object.keys(agents).length > 0 && (
        <DegradationPanel agents={agents} token={token} />
      )}

      {/* Leaderboard */}
      {leaderboard?.current && Object.keys(leaderboard.current).length > 0 && (
        <Card className="bg-gradient-to-r from-amber-950/20 to-zinc-900/50 border-amber-700/20" data-testid="leaderboard-card">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" /> Performance vs ETH Buy & Hold
              <Badge className={`ml-2 text-[10px] ${leaderboard.current.alpha >= 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                Alpha: {leaderboard.current.alpha >= 0 ? "+" : ""}{leaderboard.current.alpha?.toFixed(2)}%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 bg-zinc-900/60 border border-zinc-800">
                <p className="text-[10px] text-cyan-400 font-bold mb-1 flex items-center gap-1"><Gem className="w-3 h-3" /> Agents</p>
                <PnlText value={leaderboard.current.total_pnl} className="text-lg font-bold" />
                <p className="text-[10px] text-zinc-500 font-mono">{leaderboard.current.agent_return_pct >= 0 ? "+" : ""}{leaderboard.current.agent_return_pct?.toFixed(2)}%</p>
              </div>
              <div className="p-3 bg-zinc-900/60 border border-zinc-800">
                <p className="text-[10px] text-zinc-400 font-bold mb-1 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> ETH B&H</p>
                <p className={`font-mono text-lg font-bold ${leaderboard.current.eth_return_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {leaderboard.current.eth_return_pct >= 0 ? "+" : ""}{leaderboard.current.eth_return_pct?.toFixed(2)}%
                </p>
              </div>
              <div className={`p-3 border ${leaderboard.current.alpha >= 0 ? "bg-emerald-950/20 border-emerald-800/30" : "bg-red-950/20 border-red-800/30"}`}>
                <p className="text-[10px] text-zinc-400 font-bold mb-1">Alpha</p>
                <p className={`font-mono text-xl font-bold ${leaderboard.current.alpha >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {leaderboard.current.alpha >= 0 ? "+" : ""}{leaderboard.current.alpha?.toFixed(2)}%
                </p>
              </div>
              <div className="p-3 bg-zinc-900/60 border border-zinc-800">
                <p className="text-[10px] text-zinc-400 font-bold mb-1">Stats</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-zinc-500">Trades</span><span className="font-mono text-zinc-300">{leaderboard.current.total_trades}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Win Rate</span><span className="font-mono text-zinc-300">{leaderboard.current.win_rate?.toFixed(1)}%</span></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trade History */}
      {trades.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-400" /> Trade History
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <table className="w-full text-xs" data-testid="diamond-trades-table">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 text-left">Agent</th>
                  <th className="py-2 text-left">Side</th>
                  <th className="py-2 text-right">Entry</th>
                  <th className="py-2 text-right">Exit</th>
                  <th className="py-2 text-right">P&L</th>
                  <th className="py-2 text-left">Reason</th>
                  <th className="py-2 text-right">Closed</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={i} className="border-b border-zinc-800/50">
                    <td className="py-2 font-mono text-cyan-400">{t.strategy}</td>
                    <td className="py-2"><Badge className={`text-[10px] ${t.side === "LONG" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>{t.side}</Badge></td>
                    <td className="py-2 text-right font-mono text-zinc-300">${t.entry_price?.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono text-zinc-300">${t.exit_price?.toFixed(2)}</td>
                    <td className="py-2 text-right"><PnlText value={t.net_pnl} /></td>
                    <td className="py-2"><Badge className={`text-[10px] ${t.exit_reason === "TAKE_PROFIT" ? "bg-emerald-500/10 text-emerald-400" : t.exit_reason === "STOP_LOSS" ? "bg-red-500/10 text-red-400" : "bg-zinc-700/40 text-zinc-400"}`}>{t.exit_reason}</Badge></td>
                    <td className="py-2 text-right text-zinc-500">{t.closed_at ? new Date(t.closed_at).toLocaleString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Activity Log */}
      {agentLog.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
              <Activity className="w-4 h-4 text-zinc-400" /> Activity Log
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="diamond-agents-log">
              {agentLog.map((entry, i) => (
                <div key={i} className="flex items-center gap-3 text-[10px] py-1 border-b border-zinc-800/30">
                  <span className="text-zinc-600 w-20 shrink-0">{entry.time ? new Date(entry.time).toLocaleTimeString() : ""}</span>
                  <Badge className={`text-[9px] w-16 justify-center ${
                    entry.event === "OPEN" ? "bg-cyan-500/20 text-cyan-300" :
                    entry.event === "CLOSE" ? "bg-violet-500/20 text-violet-300" :
                    entry.event === "STARTED" ? "bg-emerald-500/20 text-emerald-300" :
                    entry.event === "STOPPED" ? "bg-red-500/20 text-red-300" :
                    "bg-zinc-700/40 text-zinc-400"
                  }`}>{entry.event}</Badge>
                  <span className="text-zinc-400 truncate">{entry.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
